import React, { createContext, useContext, useState } from 'react';

const ChatStateContext = createContext();

export const useChatState = () => {
  const context = useContext(ChatStateContext);
  if (!context) {
    throw new Error('useChatState must be used within a ChatStateProvider');
  }
  return context;
};

export const ChatStateProvider = ({ children }) => {
  const [backendChatState, setBackendChatState] = useState(null);
  const [pendingActions, setPendingActions] = useState([]);

  /**
   * Update chat state from backend API responses
   * This should ONLY be called from the 3 authoritative endpoints:
   * - /api/auth/social/google/callback/ (AuthCallback.jsx)
   * - /api/auth/user/ (AuthContext2.jsx)  
   * - /api/analyze/gsc/switch-property/ (useAnalysisState.js)
   */
  const updateFromBackend = (chatState) => {
    console.log('🎯 [ChatStateContext] Backend chat state update:', {
      chatState,
      hasState: !!chatState,
      mode: chatState?.mode,
      isOnboarding: chatState?.is_onboarding,
      propertyMatchFound: chatState?.property_match_found,
      matchedProperty: chatState?.matched_property,
      hasMessages: !!chatState?.messages,
      messageCount: chatState?.messages?.length || 0,
      errorState: chatState?.error_state,
      source: 'updateFromBackend'
    });
    
    // 🎯 STATE ANALYSIS: Log what we should show based on the backend state
    if (chatState) {
      console.log('🧠 [ChatStateContext] State Analysis:', {
        isOnboarding: chatState.is_onboarding,
        propertyMatchFound: chatState.property_match_found,
        mode: chatState.mode,
        shouldShowPropertyModal: chatState.is_onboarding && !chatState.property_match_found,
        shouldStartOnboardingTour: chatState.is_onboarding && chatState.property_match_found,
        hasErrorState: !!chatState.error_state
      });
    }

    setBackendChatState(chatState);
    
    // Determine actions based on chat state
    const actions = [];
    
    // Matrix onboarding with property selection needed
    if (chatState?.is_onboarding && !chatState?.property_match_found) {
      actions.push({ 
        type: 'show_property_modal', 
        data: { ...chatState, onboardingContext: true },
        timestamp: Date.now() 
      });
      console.log('🎯 [ChatStateContext] Action determined: show_property_modal (onboarding - no property match)');
    }
    
    // Matrix onboarding ready to proceed (property matched/selected)
    if (chatState?.is_onboarding && chatState?.property_match_found) {
      actions.push({ 
        type: 'start_onboarding_tour', 
        data: chatState,
        timestamp: Date.now() 
      });
      console.log('🎯 [ChatStateContext] Action determined: start_onboarding_tour (onboarding - property matched)');
    }
    
    if (chatState?.error_state) {
      actions.push({ 
        type: 'handle_error_state', 
        data: chatState,
        timestamp: Date.now() 
      });
      console.log('🎯 [ChatStateContext] Action determined: handle_error_state');
    }
    
    console.log('🎯 [ChatStateContext] Total actions determined:', actions.length);
    setPendingActions(actions);
  };

  /**
   * Clear pending actions after they've been processed
   */
  const clearActions = () => {
    console.log('🎯 [ChatStateContext] Clearing pending actions');
    setPendingActions([]);
  };

  const contextValue = {
    // State
    backendChatState,
    pendingActions,
    
    // Actions
    updateFromBackend,
    clearActions,
    
    // Convenience getters
    hasBackendState: !!backendChatState,
    chatMode: backendChatState?.mode || 'normal',
    isOnboarding: backendChatState?.is_onboarding || false,
    chatMessages: backendChatState?.messages || null,
    hasPendingActions: pendingActions.length > 0
  };

  return (
    <ChatStateContext.Provider value={contextValue}>
      {children}
    </ChatStateContext.Provider>
  );
};
